'use client';

import { PlainLeftArrow } from '@/assets/PlainLeftArrow';
import { PlainRightArrow } from '@/assets/PlainRightArrow';
import React, { useRef, useState } from 'react';
import Slider from 'react-slick';

import { LeftArrowIcon, RightArrowIcon } from '@/assets';
import { useIsMobile, usePrefersReducedMotion } from '@/hooks';
import slickStyles from '@/styles/slick.module.css';

type DotType =
  | 'default'
  | 'large'
  | 'large-mb-extra'
  | 'compact-dark'
  | 'compact-light';

interface ICarouselProps {
  children: React.ReactNode;
  showDots?: boolean;
  dotType?: DotType;
  dotExtraClass?: string;
  autoPlay?: boolean;
  autoPlaySpeed?: number;
  renderDotsOutside?: boolean;
  arrows?: boolean;
  useExternalArrows?: boolean;
  transitionDuration?: number;
  autoplaySpeed?: number;
}

interface ArrowProps {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

const CustomRightArrow = ({ onClick }: ArrowProps) => {
  return (
    <button
      onClick={onClick}
      className="absolute right-4 top-0 h-full z-10"
      aria-label="Next slide"
    >
      <RightArrowIcon />
    </button>
  );
};

const CustomLeftArrow = ({ onClick }: ArrowProps) => {
  return (
    <button
      onClick={onClick}
      className="absolute left-4 top-0 h-full z-10"
      aria-label="Previous slide"
    >
      <LeftArrowIcon />
    </button>
  );
};

const renderDot = (active: boolean = false, dotType: DotType = 'default') => {
  switch (dotType) {
    case 'compact-dark':
    case 'default':
      return active ? (
        <li className="w-2 h-2 bg-white rounded-full" />
      ) : (
        <li className="w-2 h-2 bg-gray-400 rounded-full" />
      );
    case 'large':
      return active ? (
        <li className="w-24 lg:w-32 h-1 bg-white my-4" />
      ) : (
        <li className="w-24 lg:w-32 h-1 bg-gray-400 my-4" />
      );
    case 'large-mb-extra':
      return active ? (
        <li className="w-24 lg:w-32 h-1 bg-white mt-4 mb-20 lg:mb-48" />
      ) : (
        <li className="w-24 lg:w-32 h-1 bg-gray-400 mt-4 mb-20 lg:mb-48" />
      );
    case 'compact-light':
      return active ? (
        <li className="w-2 h-2 bg-black rounded-full" />
      ) : (
        <li className="w-2 h-2 bg-gray-300 rounded-full" />
      );
  }
};

interface CustomDotProps {
  onClick: () => void;
  active: boolean;
  dotType: DotType;
}

const CustomDot = ({ onClick, active, dotType }: CustomDotProps) => {
  return (
    <button
      onClick={onClick}
      className={
        ['compact-dark', 'compact-light'].includes(dotType) ? 'px-1.5' : 'px-2'
      }
      aria-label={`Go to slide ${active ? 'active' : 'inactive'}`}
    >
      <ul>{renderDot(active, dotType)}</ul>
    </button>
  );
};

const CustomButtonGroup = ({
  next,
  previous,
}: {
  next?: () => void;
  previous?: () => void;
}): React.JSX.Element => {
  return (
    <div className="absolute -bottom-6 right-0 flex gap-[15px]">
      <button
        className="rounded-[50%] border-solid border-black border-[1px] w-8 h-8 flex justify-center items-center"
        onClick={() => previous?.()}
        aria-label="Previous slide"
      >
        <PlainLeftArrow fill="black" width={16} height={16} />
      </button>
      <button
        className="rounded-[50%] border-solid border-black border-[1px] w-8 h-8 flex justify-center items-center"
        onClick={() => next?.()}
        aria-label="Next slide"
      >
        <PlainRightArrow fill="black" width={16} height={16} />
      </button>
    </div>
  );
};

export const Carousel = ({
  children,
  showDots = true,
  dotType = 'default',
  dotExtraClass = '',
  useExternalArrows = false,
  transitionDuration = 500,
  renderDotsOutside = false,
  autoPlay = true,
  autoPlaySpeed,
  arrows = true,
}: ICarouselProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef<Slider>(null);
  const isMobile = useIsMobile();
  const prefersReducedMotion = usePrefersReducedMotion();

  const adjustedArrows = useExternalArrows ? false : arrows && !isMobile;

  const next = () => {
    sliderRef.current?.slickNext();
  };

  const previous = () => {
    sliderRef.current?.slickPrev();
  };

  const responsive = [
    {
      // desktop
      breakpoint: 1023,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
    {
      // tablet
      breakpoint: 464,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
    {
      // mobile
      breakpoint: 0,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ];

  const settings = {
    dots: showDots,
    infinite: true,
    speed: transitionDuration,
    slidesToShow: 1,
    slidesToScroll: 1,
    responsive,
    arrows: adjustedArrows,
    nextArrow: <CustomRightArrow />,
    prevArrow: <CustomLeftArrow />,
    // WCAG 2.2.2: never auto-advance for reduced-motion users, and pause on
    // hover/focus so others can stop it.
    autoplay: !prefersReducedMotion && Boolean(autoPlaySpeed || autoPlay),
    autoplaySpeed: autoPlaySpeed || (autoPlay ? 3000 : undefined),
    pauseOnHover: true,
    pauseOnFocus: true,
    cssEase: 'linear',
    dotsClass: `${renderDotsOutside ? 'slick-dots-carousel' : 'slick-dots-carousel justify-center'} ${dotType === 'large' || dotType === 'large-mb-extra' ? '-bottom-1' : ''} ${dotExtraClass}`,
    customPaging: (i: number) => (
      <CustomDot
        onClick={() => {}}
        active={i === currentSlide}
        dotType={dotType}
      />
    ),
    beforeChange: (oldIndex: number, newIndex: number) =>
      setCurrentSlide(newIndex),
    afterChange: (current: number) => setCurrentSlide(current),
  };

  return (
    <>
      <Slider ref={sliderRef} {...settings} className={slickStyles.slider}>
        {children}
      </Slider>
      {useExternalArrows && (
        <CustomButtonGroup next={next} previous={previous} />
      )}
    </>
  );
};
